import { Box, Button, CircularProgress, Skeleton } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { useDispatch, useSelector } from 'react-redux';
import { getGpsrProductTypes, getGpsrChangeServiceIsLoad } from 'redux/reducers/gpsr/selectors';
import {
	confirmGpsrAdditionalInformationRequest,
	getGpsrProductTypesRequest,
} from 'redux/reducers/gpsr/reducer';
import { IGpsrTypesItem } from 'redux/reducers/gpsr/types';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { IRequestWithPageSortFilterSearch } from 'services/constants/types';
import { getFiltersFromNavigateParams, getOrderByFromNavigateParams } from 'utils/FilterParams';
import { PATHS } from 'routes/constants';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import {
	getEudrInformation,
	getEudrStatus,
	getEudrStatusIsLoading,
	getEudrInformationIsLoading,
} from 'redux/reducers/eudr/selectors';
import { getEudrInformationRequest, getEudrStatusRequest } from 'redux/reducers/eudr/reducer';
import { NoEudrBanner } from './NoEudrBanner';
import { eudrAdditionalInformationStructure } from './eudrAdditionalInformationStructure';
import { gpsrTypesTableStructure } from './tableStructure/gpsrTypesTableStructure';
import { EasTableInfoPannel, TEasTableInfoPannelData } from '../GpsrService/EasTableInfoPannel';
import { NoEudrProductsBanner } from './NoEudrProductsBanner';
import { ChangeEudrServiceDialog } from './ChangeEudrServiceDialog';
import {
	getDashboardServices,
	getDashboardServicesLoader,
} from 'redux/reducers/dashboard/selectors';

export const EudrService: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const dashboardServices = useSelector(getDashboardServices);
	const dashboardServicesIsLoad = useSelector(getDashboardServicesLoader);
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const items = useSelector(getGpsrProductTypes);
	const eudrInformation = useSelector(getEudrInformation);
	const eudrInformationIsLoading = useSelector(getEudrInformationIsLoading);
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	const [openChangeEudrServiceDialog, setOpenChangeEudrServiceDialog] = useState(false);
	const [openConfirmSubscriptionDetailsDialog, setOpenConfirmSubscriptionDetailsDialog] =
		useState(false);
	const gpsrChangeServiceIsLoad = useSelector(getGpsrChangeServiceIsLoad);
	const tableStructure: TTableStructure<keyof IGpsrTypesItem>[] =
		gpsrTypesTableStructure as TTableStructure<keyof IGpsrTypesItem>[];

	const getEudrRequestParams = (): IRequestWithPageSortFilterSearch => ({
		pageNumber: page ? +page : 1,
		pageSize: PAGE_SIZE,
		searchString: String(navigateParams?.search ?? ''),
		filters: getFiltersFromNavigateParams(tableStructure, navigateParams),
		orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams),
	});

	useEffect(() => {
		dispatch(getGpsrProductTypesRequest(getEudrRequestParams()));
		dispatch(getEudrInformationRequest());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleLink = (id: number) => {
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`${PATHS.EUDR_SERVICE_PAGE}/${id}`);
	};

	const handleEudrActions = (productTypeId: number, typeOfDialog: string) => {
		if (typeOfDialog === 'Public Page') {
			window.open(items?.data.find((item) => item.id === productTypeId)?.doCLink ?? '', '_blank');
			// gpsrSku?.product_type?.doCLink ?? '', '_blank');
		}
	};

	const handleCancelSubscription = () => {
		// dispatch(
		// 	getEudrCancellationRequest({
		// 		gpsrRequest: {
		// 			...getEudrRequestParams(),
		// 		},
		// 	}),
		// );
	};

	const handleOpenCancelDialog = () => {
		setOpenCancelDialog(true);
	};

	const handleConfirmSubscriptionDetails = () => {
		dispatch(confirmGpsrAdditionalInformationRequest());
	};

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			{dashboardServicesIsLoad && <Skeleton variant="rectangular" height={530} />}
			{!dashboardServicesIsLoad && !dashboardServices?.eudrEnabled && <NoEudrBanner />}
			{dashboardServices?.eudrEnabled && ((items && items?.data?.length > 0) || navigateParams) && (
				<EasTableWithFilter<IGpsrTypesItem>
					tableStructure={gpsrTypesTableStructure as TTableStructure<keyof IGpsrTypesItem>[]}
					items={items}
					itemsIsLoading={dashboardServicesIsLoad}
					handleAction={handleEudrActions}
					handleLink={handleLink}
					highlightedRow={null}
					showButtons={items?.data.map((item) => {
						return {
							'Public Page': item.status !== 'No Declaration',
						};
					})}
					filterChildren={
						<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
							<Button
								variant="contained"
								color="primary"
								size="small"
								onClick={() => navigate(PATHS.EUDR_FILES_PAGE)}
							>
								Uploaded EUDR files
							</Button>
							{eudrInformation?.allowRegistration && (
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={() => setOpenConfirmSubscriptionDetailsDialog(true)}
									disabled={!!gpsrChangeServiceIsLoad}
								>
									{gpsrChangeServiceIsLoad ? (
										<CircularProgress size={20} />
									) : (
										'Confirm Subscription Details'
									)}
								</Button>
							)}
							{/* <Button
								variant="contained"
								color="primary"
								// onClick={() => navigate(PATHS.CHANGE_GPSR_SERVICE_PAGE)}
								size="small"
								onClick={() => setOpenChangeEudrServiceDialog(true)}
							>
								Change EUDR Service
							</Button> */}

							{eudrInformation?.allowRegistration && (
								<Button
									variant="contained"
									color="primary"
									onClick={handleOpenCancelDialog}
									size="small"
								>
									Disable auto-renewal
								</Button>
							)}
						</Box>
					}
					minWidth={1000}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							gap: 1,
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<EasTableInfoPannel
							structure={eudrAdditionalInformationStructure}
							data={eudrInformation as unknown as TEasTableInfoPannelData}
						/>
					</Box>
				</EasTableWithFilter>
			)}
			{!dashboardServicesIsLoad &&
				dashboardServices?.eudrEnabled &&
				items?.data?.length === 0 &&
				!navigateParams && <NoEudrProductsBanner />}
			<ConfirmationDialog
				open={openCancelDialog}
				setClose={() => setOpenCancelDialog(false)}
				handleConfirm={() => {
					handleCancelSubscription();
					setOpenCancelDialog(false);
				}}
				title="Disable auto-renewal of the Subscription"
				text="Are you sure you want to disable auto-renewal of your EUDR subscription?"
			/>
			<ChangeEudrServiceDialog
				open={openChangeEudrServiceDialog}
				setClose={() => setOpenChangeEudrServiceDialog(false)}
			/>
			<ConfirmationDialog
				open={openConfirmSubscriptionDetailsDialog}
				setClose={() => setOpenConfirmSubscriptionDetailsDialog(false)}
				handleConfirm={() => {
					handleConfirmSubscriptionDetails();
					setOpenConfirmSubscriptionDetailsDialog(false);
				}}
				title="Confirm EUDR Subscription Details"
				text="Are you sure you want to confirm your EUDR subscription details?"
			/>
		</Box>
	);
};
